import axios from 'axios';
import InternalServerErrorAlert from './Swals/InternalServerErrorAlert';
import router from '~/router';
import store from '~/store';

const AUTH_FLOW_PATHS = [
    '/api/login',
    '/api/login/verify-otp',
    '/api/login/resend-otp',
    '/api/password/email',
    '/api/password/reset',
];

const GUEST_AUTH_ROUTE_NAMES = [
    'login',
    'login.verify',
    'password.reset',
    'password.reset.token',
];

function isAuthFlowRequest(url = '') {
    return AUTH_FLOW_PATHS.some(path => url.includes(path));
}

function isGuestAuthRoute() {
    const routeName = router.currentRoute && router.currentRoute.name;

    return GUEST_AUTH_ROUTE_NAMES.includes(routeName);
}

// Get the base_url
let getUrl = window.location;
let baseUrl = getUrl .protocol + "//" + getUrl.host; // + "/" + getUrl.pathname.split('/')[1];

// Automatically add CSRF token to every outgoing request
const baseURL = baseUrl;
const headers = {
    'X-CSRF-TOKEN': document.head.querySelector('meta[name="csrf-token"]').content,
    'X-Requested-With': 'XMLHttpRequest',
};

const Parent = axios.create({
    baseURL,
    headers,
});

// Request Interceptor
Parent.interceptors.request.use(request => {
    const requestUrl = request.url || '';

    if (!isAuthFlowRequest(requestUrl) && store.getters.authToken) {
        request.headers.common['Authorization'] = `Bearer ${store.getters.authToken}`;
    } else {
        // Else JWT Auth will crash in back-end
        request.headers.common['Authorization'] = ``;
    }

    return request;
});

// Response interceptor
Parent.interceptors.response.use(response => httpSuccess(response), error => httpFail(error));

function httpSuccess(response) {
    if (response.headers.authorization) {
        store.dispatch('saveToken', {token: response.headers.authorization.replace('Bearer ', '')});
    }

    return Promise.resolve(response);
}

function httpFail(error) {
    // Reject on Laravel-driven validation errors
    if (error.response && error.response.status === 422) {
        return Promise.reject(error);
    }

    // Refresh tokens and reject to be further handled be the request initiator
    if (error.response && error.response.status === 419) {
        // We don't use CSRF tokens :P
        // return refreshAppTokens().then(() => Promise.reject(error));
        return Promise.reject(error);
    }

    // If internal error
    if (error.message && !error.response) {
        // Due to a possible bug in Laravel Echo, whitelist Echo server error
        // See explanation above
        if (error.message === 'Cannot read property \'socketId\' of undefined') {
            // showError(error.message);
            return Promise.resolve(error);
        }

        // Display any other errors to the user and reject
        showInternalError(error);
        return Promise.reject(error);
    }

    // Redirect to log in page if unauthenticated
    if (error.response && error.response.status === 401) {
        const requestUrl = error.config && error.config.url ? error.config.url : '';

        if (!isAuthFlowRequest(requestUrl) && !isGuestAuthRoute()) {
            window.vue.$snotify.info(uLang('app.token_expired'), uLang('app.token_expired'));

            // await store.dispatch('logout');
            store.dispatch('resetUser');

            router.push({name: 'login'});
        }

        return Promise.reject(error);
    }

    // Redirect if the backend asks it
    if (error.response && error.response.status === 302 && error.response.data.redirect) {
        router.push(error.response.data.redirect);
        return Promise.reject(error);
    }

    // Show all other errors
    // showHttpError(error);
    return Promise.reject(error);
}

function showInternalError(error) {
    console.error(error);
    new InternalServerErrorAlert().show();
}

function refreshAppTokens() {
    // Retrieve a new page with a fresh token
    axios.get('/')
        .then(({data}) => {
            const wrapper = document.createElement('div');
            wrapper.innerHTML = data;

            return div.querySelector('meta[name=csrf-token]').getAttribute('content');
        })
        .then((token) => {
            axios.defaults.headers['X-CSRF-TOKEN'] = token;
            document.querySelector('meta[name=csrf-token]').setAttribute('content', token);
        });
}

class Http extends Parent {
    static post(url, data, config) {
        return new Promise((resolve, reject) => {
            Parent.post(url, data, config)
                .then(response1 => resolve(response1))
                .catch((error1) => {
                    // There is one more try for token mismatch error
                    if (error1.response && error1.response.status === 419) {
                        Parent.post(url, data, config)
                            .then((response2) => {
                                const u = new window.URL(decodeURIComponent(location.href));
                                location.href = `${location.origin}${u.searchParams.get('back')}`;
                                return resolve(response2);
                            })
                            .catch(error2 => reject(error2));
                    }

                    return reject(error1);
                });
        });
    }

    static put(url, data, config) {
        // Attach _method property for Laravel
        data = Http.addMethodField(data, 'PUT');

        return Http.post(url, data, config);
    }

    static patch(url, data, config) {
        // Attach _method property for Laravel
        data = Http.addMethodField(data, 'PATCH');

        return Http.post(url, data, config);
    }

    static delete(url, config) {
        // Send request with _method property for Laravel
        return Http.post(url, {_method: 'DELETE'}, config);
    }

    static addMethodField(data, requestType) {
        if (data instanceof FormData) {
            data.append('_method', requestType);
        } else {
            data['_method'] = requestType;
        }

        return data;
    }
}

export default Http;
